import React, { useContext, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
  Button,
  Tooltip,
  Chip,
  Rating,
  IconButton as MuiIconButton,
  Badge,
} from "@mui/material";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { AuthContext } from "context/Authentication";
import { ThemeContext } from "context/ThemeContext";
import { redirectToLoginWithReturn } from "utils/authUtils";
import {
  postRecord,
  handleApiResponse,
  handleApiError,
} from "services/services";
import SnackBar from "CommonComponents/Snackbar/SnackBar.jsx";
import ProductQuickView from "CommonComponents/QuickViews/ProductQuickView.jsx";
import ProductChoiceSelector from "CommonComponents/ProductChoiceSelector/ProductChoiceSelector.jsx";
import ProductAddonsSelector from "CommonComponents/ProductAddonsSelector/ProductAddonsSelector.jsx";
import { addToGuestCart } from "utils/guestCartOperations";

const API_AddToCart = "cart/create";
const API_UpdateCart = "cart/update";
const API_GetCartByUserId = "cart/getbyuserid";
const API_AddToWishlist = "wishlist/create";
const API_DeleteWishlist = "wishlist/delete";
const API_GetWishlistByUserId = "wishlist/getbyuserid";

export default function ProductCard({
  product,
  headerRefresh,
  setHeaderRefresh,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const themeMode = useContext(ThemeContext);
  const context = useContext(AuthContext);
  const darkMode = themeMode.state.darkMode;
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackOptions, setSnackOptions] = useState({
    color: "success",
    message: "Hi",
  });
  const [processing, setProcessing] = useState(false);
  const [wishlist, setWishlist] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [showChoiceSelector, setShowChoiceSelector] = useState(false);
  const [showAddonSelector, setShowAddonSelector] = useState(false);
  const [selectedChoices, setSelectedChoices] = useState({});
  const [units] = useState(1); // Default quantity for product cards

  useEffect(() => {
    let isMounted = true;

    if (context.state.isAuthenticated) {
      fetchWishlist(isMounted);
    }

    return () => {
      isMounted = false;
    };
  }, [context.state.isAuthenticated]);

  // Smooth image shuffling effect with longer duration
  useEffect(() => {
    let isMounted = true;

    if (!product.Default_Media || product.Default_Media.length <= 1) {
      return;
    }

    const interval = setInterval(() => {
      if (!isMounted) {
        clearInterval(interval);
        return;
      }

      setFade(false);
      setTimeout(() => {
        if (!isMounted) return;

        setCurrentImageIndex((prevIndex) =>
          prevIndex === product.Default_Media.length - 1 ? 0 : prevIndex + 1
        );

        setTimeout(() => {
          if (!isMounted) return;
          setFade(true);
        }, 200); // Smooth fade-in delay
      }, 800); // Longer fade-out duration
    }, 4000); // Increased interval duration

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [product.Default_Media?.length]);

  const fetchWishlist = async (mountedState) => {
    try {
      const response = await postRecord(API_GetWishlistByUserId, {
        User_Id: context.state.user.User_Id,
      });
      if (mountedState && response.status === 200 && response.data) {
        setWishlist(response.data);
      }
    } catch (error) {
      if (mountedState) {
        console.log(error);
      }
    }
  };

  const resetAndPreview = () => {
    setHeaderRefresh(true);
  };

  const addToCart = async () => {
    const hasChoices = hasChoiceOptions();
    const choicesSelected = Object.keys(selectedChoices).length > 0;

    // If product has choice options and they haven't been selected yet
    if (hasChoices && !choicesSelected) {
      setShowChoiceSelector(true);
      return;
    }

    // If product addons feature is enabled, check if this product actually has addons
    if (isProductAddonsEnabled()) {
      try {
        const availableAddons = await checkProductAddons();
        if (availableAddons && availableAddons.length > 0) {
          setShowAddonSelector(true);
          return;
        }
      } catch (error) {}
    }

    // Otherwise proceed with direct cart addition (works for both auth and guest)
    await proceedWithCartFlow();
  };

  const performDirectCartAdd = async (
    choiceOptions = [],
    forceCreateNew = false
  ) => {
    let isMounted = true;

    try {
      // Skip existing item lookup when choice options are present or forceCreateNew is true
      const shouldCheckExisting =
        !forceCreateNew &&
        (!choiceOptions || Object.keys(choiceOptions).length === 0);
      let existingCartItem = null;

      if (shouldCheckExisting) {
        const cartResponse = await postRecord(API_GetCartByUserId, {
          User_Id: context.state.user.User_Id,
        });

        if (!isMounted) return;

        if (cartResponse.status === 200 && cartResponse.data) {
          existingCartItem = cartResponse.data.find(
            (item) =>
              item.Combination_Id === product.Default_Combination.Combination_Id
          );
        }
      }

      if (existingCartItem) {
        const updatedCartItem = {
          ...existingCartItem,
          Units: existingCartItem.Units + 1,
          Total_Price:
            (existingCartItem.Units + 1) * existingCartItem.Price_Per_Unit,
          Choice_Options:
            choiceOptions && Object.keys(choiceOptions).length > 0
              ? choiceOptions
              : null,
          Modified_By: context.state.user.User_Id,
        };

        try {
          const response = await postRecord(API_UpdateCart, updatedCartItem);
          if (isMounted) {
            handleApiResponse(
              response,
              setSnackOptions,
              setProcessing,
              setSnackOpen,
              resetAndPreview
            );
          }
        } catch (error) {
          if (isMounted) {
            handleApiError(error, setSnackOptions, setProcessing, setSnackOpen);
          }
        }
      } else {
        const newCartItem = {
          User_Id: context.state.user.User_Id,
          Product_Id: product.Product_Id,
          Combination_Id: product.Default_Combination.Combination_Id,
          Units: 1,
          Price_Per_Unit: product.Default_Inventory.Total_Selling_Price,
          Total_Price: product.Default_Inventory.Total_Selling_Price,
          Is_Savefor_Later: "0",
          Choice_Options:
            choiceOptions && Object.keys(choiceOptions).length > 0
              ? choiceOptions
              : null,
          Added_By: context.state.user.User_Id,
        };

        try {
          const response = await postRecord(API_AddToCart, newCartItem);
          if (isMounted) {
            handleApiResponse(
              response,
              setSnackOptions,
              setProcessing,
              setSnackOpen,
              resetAndPreview
            );
          }
        } catch (error) {
          if (isMounted) {
            handleApiError(error, setSnackOptions, setProcessing, setSnackOpen);
          }
        }
      }
    } catch (error) {
      if (isMounted) {
        handleApiError(error, setSnackOptions, setProcessing, setSnackOpen);
      }
    }

    return () => {
      isMounted = false;
    };
  };

  const toggleWishlist = async () => {
    if (!context.state.isAuthenticated) {
      setSnackOptions({
        color: "warning",
        message: "Please log in or register to add items to the cart.",
      });
      setSnackOpen(true);
      setTimeout(() => {
        const currentPath = location.pathname + location.search;
        redirectToLoginWithReturn(navigate, currentPath);
      }, 2000);
      return;
    }

    let isMounted = true;

    const isInWishlist = wishlist.some(
      (item) => item.Product_Id === product.Product_Id
    );

    if (isInWishlist) {
      const wishlistItem = wishlist.find(
        (item) => item.Product_Id === product.Product_Id
      );

      try {
        const response = await postRecord(API_DeleteWishlist, {
          Wishlist_Id: wishlistItem.Wishlist_Id,
          Deleted_By: context.state.user.User_Id,
        });
        if (isMounted) {
          handleApiResponse(
            response,
            setSnackOptions,
            setProcessing,
            setSnackOpen,
            resetAndPreview
          );
          fetchWishlist();
        }
      } catch (error) {
        if (isMounted) {
          handleApiError(error, setSnackOptions, setProcessing, setSnackOpen);
        }
      }
    } else {
      const newWishlistItem = {
        Wishlist_Name: "My Wishlist",
        User_Id: context.state.user.User_Id,
        Product_Id: product.Product_Id,
        Combination_Id: product.Default_Combination.Combination_Id,
        Added_By: context.state.user.User_Id,
      };

      const isCombinationInWishlist = wishlist.some(
        (item) =>
          item.Combination_Id === product.Default_Combination.Combination_Id
      );

      if (isCombinationInWishlist) {
        alert("This combination is already in your wishlist.");
        return;
      }

      try {
        const response = await postRecord(API_AddToWishlist, newWishlistItem);
        if (isMounted) {
          handleApiResponse(
            response,
            setSnackOptions,
            setProcessing,
            setSnackOpen,
            resetAndPreview
          );
          fetchWishlist();
        }
      } catch (error) {
        if (isMounted) {
          handleApiError(error, setSnackOptions, setProcessing, setSnackOpen);
        }
      }
    }

    return () => {
      isMounted = false;
    };
  };

  const isInWishlist = wishlist.some(
    (item) => item.Product_Id === product.Product_Id
  );

  // Helper functions for choice options and addons
  const hasChoiceOptions = () => {
    if (!product?.Choice_Options) return false;
    try {
      let parsed;
      if (typeof product.Choice_Options === "string") {
        parsed = JSON.parse(product.Choice_Options);
      } else {
        parsed = product.Choice_Options;
      }

      // Check for new nested format: [{name: 'Size', options: ['S', 'M', 'L']}]
      if (Array.isArray(parsed)) {
        return (
          parsed.length > 0 &&
          parsed.some(
            (choice) =>
              choice.name &&
              Array.isArray(choice.options) &&
              choice.options.length > 0
          )
        );
      }

      return false;
    } catch (error) {
      console.error("Error parsing choice options:", error);
      return false;
    }
  };

  // Check if Product Addons feature is enabled in app settings
  const isProductAddonsEnabled = () => {
    if (!context.state.appSettings) return false;
    const addonSetting = context.state.appSettings.find(
      (setting) =>
        setting.Setting_Name === "Product Addons" &&
        setting.Setting_Value === "Enabled" &&
        setting.Is_Active === "1"
    );
    return !!addonSetting;
  };

  // Check if product has available addons
  const checkProductAddons = async () => {
    try {
      const response = await postRecord(
        "product-addons/getbyproductid-enhanced",
        {
          Product_Id: product.Product_Id,
          Combination_Id: product.Default_Combination.Combination_Id,
        }
      );

      if (
        response.status === 200 &&
        response.data &&
        response.data.length > 0
      ) {
        return response.data;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  const handleChoicesSelected = (choices) => {
    // This function is now only used as a fallback
    // The main cart addition happens directly in ProductChoiceSelector
    setSelectedChoices(choices);
    setShowChoiceSelector(false);

    // Check if addons are available (this path is mainly for when choices are selected but cart addition failed)
    if (isProductAddonsEnabled()) {
      try {
        checkProductAddons().then((availableAddons) => {
          if (availableAddons && availableAddons.length > 0) {
            setShowAddonSelector(true);
          }
        });
      } catch (error) {}
    }
  };

  const handleAddonCartComplete = () => {
    setShowAddonSelector(false);
    setSelectedChoices({});
    resetAndPreview();
  };

  const proceedWithCartFlow = async () => {
    // This is the original addToCart logic
    const choiceOptions =
      Object.keys(selectedChoices).length > 0 ? selectedChoices : [];
    const forceCreateNew = Object.keys(selectedChoices).length > 0;
    await performCartAdd(choiceOptions, forceCreateNew);
  };

  // Wrapper function to handle both authenticated and guest cart flows
  const performCartAdd = async (choiceOptions = [], forceCreateNew = false) => {
    if (!context.state.isAuthenticated) {
      // Temporarily disable guest checkout from product card
      setSnackOptions({
        color: "warning",
        message: "Please Login/Register to order"
      });
      setSnackOpen(true);
      
      // Delay navigation slightly to let snackbar show
      setTimeout(() => {
        navigate('/Login');
      }, 1500);
      
      return;
      
      /* Guest cart functionality disabled
      // Guest cart flow
      try {
        // Keep choiceOptions in object format for API compatibility
        const choiceOptionsForGuest = choiceOptions || {};

        // Prepare product details for guest cart - match authenticated cart structure
        const productDetails = {
          // Core product info
          Product_Full_Name: product.Product_Full_Name,
          Short_Description: product.Short_Description || "",
          Product_Images: product.Product_Images || product.Default_Media || [],
          Default_Media: product.Default_Media || [], // Include Default_Media for display

          // Image handling - get first available image
          Product_Image_1: (() => {
            if (
              product.Product_Images &&
              Array.isArray(product.Product_Images) &&
              product.Product_Images.length > 0
            ) {
              return product.Product_Images[0];
            }
            if (
              product.Default_Media &&
              Array.isArray(product.Default_Media) &&
              product.Default_Media.length > 0
            ) {
              return product.Default_Media[0];
            }
            return null;
          })(),

          // Pricing and inventory
          Total_Selling_Price:
            product.Default_Inventory?.Total_Selling_Price || 0,
          Current_Stock: product.Default_Inventory?.Current_Stock || 0,

          // Combination details
          Combination_Id: product.Default_Combination?.Combination_Id,
          Combination_Text: product.Default_Combination?.Combination_Text || "",

          // Product status flags
          Is_Deleted: product.Is_Deleted || "0",
          Is_Soldout: product.Default_Combination?.Is_Soldout || "0",

          // Store full objects for compatibility
          Default_Inventory: product.Default_Inventory,
          Default_Combination: product.Default_Combination,
          Product: {
            Product_Full_Name: product.Product_Full_Name,
            Short_Description: product.Short_Description || "",
            Is_Deleted: product.Is_Deleted || "0",
          },
          Inventory: product.Default_Inventory,
          Combination: product.Default_Combination,
        };

        // Add to guest cart
        const success = await addToGuestCart({
          productId: product.Product_Id,
          quantity: 1,
          choiceOptions: choiceOptionsForGuest,
          addons: [],
          productDetails,
          dispatch: context.dispatch,
          showSnackbar: (message, severity) => {
            setSnackOptions({
              color: severity,
              message: message,
            });
            setSnackOpen(true);
          },
        });

        if (success) {
          // Reset selections after successful add
          setSelectedChoices({});
          setHeaderRefresh?.(!headerRefresh);
        }
      } catch (error) {
        console.error("Error adding to guest cart:", error);
        setSnackOptions({
          color: "error",
          message: "Failed to add item to cart",
        });
        setSnackOpen(true);
      }
      */
    } else {
      // Authenticated user flow
      await performDirectCartAdd(choiceOptions, forceCreateNew);
    }
  };

  // Helper functions for combination features
  const getOtherCombinations = () => {
    if (
      !product.other_combinations ||
      product.other_combinations.length === 0
    ) {
      return [];
    }
    return product.other_combinations;
  };

  const getAllCombinations = () => {
    const allCombos = [];
    if (product.Default_Combination?.Combination_Text) {
      allCombos.push(product.Default_Combination.Combination_Text);
    }
    if (product.other_combinations && product.other_combinations.length > 0) {
      allCombos.push(...product.other_combinations);
    }
    return allCombos;
  };

  // Enhanced tooltip content with chip-based combinations display
  const getCombinationTooltipContent = () => {
    const otherCombos = getOtherCombinations();
    if (otherCombos.length === 0) return "Only one combination available";

    return (
      <Box sx={{ maxWidth: 300 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
          {otherCombos.map((combo, index) => (
            <Chip
              key={index}
              label={combo}
              size="small"
              sx={{
                fontSize: "0.7rem",
                height: 24,
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(255, 255, 255, 0.9)",
                color: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.9)"
                    : "rgba(0, 0, 0, 0.8)",
                border: "1px solid",
                borderColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.2)"
                    : "rgba(0, 0, 0, 0.1)",
                "& .MuiChip-label": {
                  px: 1,
                  py: 0.25,
                },
              }}
            />
          ))}
        </Box>
      </Box>
    );
  };

  const renderCombinationChip = (combinationText, isDefault = false) => {
    return (
      <Typography
        sx={{
          fontSize: isDefault ? "0.75rem" : "0.7rem",
          fontWeight: isDefault ? 600 : 500,
          color: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.9)"
              : "rgba(0, 0, 0, 0.8)",
        }}
      >
        {combinationText}
        {isDefault && " (Default)"}
      </Typography>
    );
  };

  const handleQuickView = (e) => {
    e.stopPropagation();
    setQuickViewOpen(true);
  };

  return (
    <Card
      sx={{
        borderRadius: 4,
        overflow: "hidden",
        height: "100%",
        minHeight: { xs: 320, sm: 400 }, // Smaller minimum height on mobile
        display: "flex",
        flexDirection: "column",
        position: "relative",
        cursor: "pointer",
        background: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(18, 18, 20, 0.95)"
            : "rgba(255, 255, 255, 0.98)",
        backdropFilter: "blur(20px)",
        border: "1px solid",
        borderColor: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.08)"
            : "rgba(0, 0, 0, 0.06)",
        boxShadow: (theme) =>
          theme.palette.mode === "dark"
            ? "0 4px 24px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2)"
            : "0 4px 24px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.03)",
        transform: "translateY(0)",
        transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: (theme) =>
            theme.palette.mode === "dark"
              ? `linear-gradient(135deg, ${theme.palette.primary.main}08 0%, ${theme.palette.secondary.main}06 100%)`
              : `linear-gradient(135deg, ${theme.palette.primary.main}04 0%, ${theme.palette.secondary.main}03 100%)`,
          opacity: 0,
          transition: "opacity 0.3s ease",
          zIndex: 0,
        },
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0 12px 40px rgba(0, 0, 0, 0.5), 0 6px 16px rgba(0, 0, 0, 0.3)"
              : "0 12px 40px rgba(0, 0, 0, 0.12), 0 6px 16px rgba(0, 0, 0, 0.08)",
          borderColor: (theme) =>
            theme.palette.mode === "dark"
              ? `${theme.palette.primary.main}40`
              : `${theme.palette.primary.main}20`,
          "&::before": {
            opacity: 1,
          },
          "& .product-image": {
            transform: "scale(1.03)",
          },
          "& .product-actions": {
            opacity: 1,
            transform: "translateY(0)",
          },
        },
        "& > *": {
          position: "relative",
          zIndex: 1,
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          height: { xs: 180, sm: 220 }, // Smaller height on mobile
          overflow: "hidden",
          cursor: "pointer",
        }}
        onClick={() => {
          // Use the actual Combination_Text from the default combination
          const combinationText = product?.Default_Combination?.Combination_Text
            ? product.Default_Combination.Combination_Text.toLowerCase().replace(
                /[,\s]+/g,
                "-"
              ) // Replace commas and spaces with hyphens
            : "default";

          const combinationId =
            product?.Default_Combination?.Combination_Id || "";

          navigate(
            encodeURI(
              `/ProductView/${product.Product_Full_Name.toLowerCase()}/${combinationText}/${combinationId}`
            )
          );
        }}
      >
        <Box
          component="img"
          src={
            product?.Default_Media && product.Default_Media[currentImageIndex]
              ? process.env.BASEURL + product.Default_Media[currentImageIndex]
              : ""
          }
          alt={product.Product_Full_Name}
          className="product-image"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: fade ? 1 : 0,
            transition: "all 1s cubic-bezier(0.4, 0, 0.2, 1)",
            filter: (theme) =>
              theme.palette.mode === "dark"
                ? "brightness(0.95) contrast(1.05)"
                : "brightness(1) contrast(1)",
          }}
        />

        {/* Quick action buttons overlay - bottom right */}
        <Box
          sx={{
            position: "absolute",
            bottom: 12,
            right: 12,
            display: "flex",
            flexDirection: "row",
            gap: 0.75,
            opacity: 0,
            transform: "translateY(8px)",
            transition: "all 0.25s ease",
            ".MuiCard-root:hover &": {
              opacity: 1,
              transform: "translateY(0)",
            },
            zIndex: 3,
          }}
        >
          <Tooltip title="Quick view" placement="top">
            <IconButton
              size="small"
              sx={{
                width: 32,
                height: 32,
                background: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(0, 0, 0, 0.6)"
                    : "rgba(255, 255, 255, 0.8)",
                backdropFilter: "blur(12px)",
                border: "1px solid",
                borderColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.1)",
                color: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.9)"
                    : "rgba(0, 0, 0, 0.8)",
                boxShadow: (theme) =>
                  theme.palette.mode === "dark"
                    ? "0 4px 20px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
                    : "0 4px 20px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8)",
                transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                "&:hover": {
                  transform: "translateY(-2px) scale(1.05)",
                  backgroundColor: "primary.main",
                  color: "white",
                  borderColor: "primary.main",
                  boxShadow: (theme) =>
                    theme.palette.mode === "dark"
                      ? "0 8px 25px rgba(0, 0, 0, 0.6), 0 0 20px rgba(25, 118, 210, 0.4)"
                      : "0 8px 25px rgba(25, 118, 210, 0.3), 0 0 20px rgba(25, 118, 210, 0.2)",
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleQuickView(e);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={isInWishlist ? "Remove from wishlist" : "Add to wishlist"}
            placement="top"
          >
            <IconButton
              size="small"
              sx={{
                width: 32,
                height: 32,
                background: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(0, 0, 0, 0.6)"
                    : "rgba(255, 255, 255, 0.8)",
                backdropFilter: "blur(12px)",
                border: "1px solid",
                borderColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.1)",
                color: isInWishlist
                  ? "secondary.main"
                  : (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.9)"
                        : "rgba(0, 0, 0, 0.8)",
                boxShadow: (theme) =>
                  theme.palette.mode === "dark"
                    ? "0 4px 20px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
                    : "0 4px 20px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8)",
                transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                "&:hover": {
                  transform: "translateY(-2px) scale(1.05)",
                  backgroundColor: "secondary.main",
                  color: "white",
                  borderColor: "secondary.main",
                  boxShadow: (theme) =>
                    theme.palette.mode === "dark"
                      ? "0 8px 25px rgba(0, 0, 0, 0.6), 0 0 20px rgba(156, 39, 176, 0.4)"
                      : "0 8px 25px rgba(156, 39, 176, 0.3), 0 0 20px rgba(156, 39, 176, 0.2)",
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                toggleWishlist();
              }}
            >
              {isInWishlist ? (
                <FavoriteIcon fontSize="small" />
              ) : (
                <FavoriteBorderIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>

        {/* Vendor name overlay */}
        {context.state?.companyInfo?.Is_Aggregator === "1" &&
          product.Vendor_Name && (
            <Chip
              label={product.Vendor_Name}
              size="small"
              sx={{
                position: "absolute",
                bottom: 12,
                left: 12,
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(0, 0, 0, 0.85)"
                    : "rgba(255, 255, 255, 0.9)",
                color: "primary.main",
                backdropFilter: "blur(12px)",
                fontWeight: 500,
                fontSize: "0.7rem",
                "& .MuiChip-label": {
                  px: 1.5,
                  py: 0.25,
                },
                boxShadow: (theme) =>
                  theme.palette.mode === "dark"
                    ? "0 2px 8px rgba(0, 0, 0, 0.4)"
                    : "0 2px 8px rgba(0, 0, 0, 0.1)",
                border: "1px solid",
                borderColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? `${theme.palette.primary.main}30`
                    : `${theme.palette.primary.main}20`,
              }}
            />
          )}

        {/* Discount badge */}
        {product?.Default_Inventory?.Total_MRP_Price &&
          product.Default_Inventory.Total_MRP_Price >
            product?.Default_Inventory?.Total_Selling_Price && (
            <Box
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 10,
                backgroundColor: "rgba(255, 87, 34, 0.95)",
                color: "white",
                padding: "4px 8px",
                borderRadius: "12px",
                fontSize: "0.7rem",
                fontWeight: "bold",
                backdropFilter: "blur(8px)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
              }}
            >
              {Math.round(
                ((product.Default_Inventory.Total_MRP_Price -
                  product.Default_Inventory.Total_Selling_Price) /
                  product.Default_Inventory.Total_MRP_Price) *
                  100
              )}
              % OFF
            </Box>
          )}

        {/* Mobile ratings overlay - only visible on mobile */}
        <Box
          sx={{
            display: { xs: "flex", sm: "none" }, // Only show on mobile
            position: "absolute",
            bottom: 8,
            left: 8,
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            color: "white",
            padding: "4px 8px",
            borderRadius: "12px",
            backdropFilter: "blur(8px)",
            alignItems: "center",
            gap: 0.5,
            zIndex: 5,
          }}
        >
          <Rating
            value={product.averageRating || 0}
            precision={0.5}
            readOnly
            size="small"
            sx={{
              fontSize: "0.7rem",
              "& .MuiRating-iconFilled": {
                color: "#ffc107",
              },
              "& .MuiRating-iconEmpty": {
                color: "rgba(255, 255, 255, 0.3)",
              },
            }}
          />
          {product.averageRating > 0 && (
            <Typography
              variant="caption"
              sx={{ fontSize: "0.65rem", fontWeight: 500 }}
            >
              {product.averageRating.toFixed(1)}
            </Typography>
          )}
        </Box>
      </Box>

      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          p: 2,
          pb: 1.5, // Slightly less bottom padding
        }}
      >
        <Tooltip
          title={product.Product_Full_Name}
          placement="top"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                fontSize: "0.8rem",
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(40, 40, 40, 0.98)"
                    : "rgba(60, 60, 60, 0.98)",
                backdropFilter: "blur(12px)",
                border: (theme) =>
                  theme.palette.mode === "dark"
                    ? "1px solid rgba(255, 255, 255, 0.1)"
                    : "1px solid rgba(255, 255, 255, 0.2)",
                borderRadius: "8px",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
                p: 1,
              },
            },
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              display: "-webkit-box",
              WebkitLineClamp: 1,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              mb: 0.5,
              fontSize: "0.95rem",
              color: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.95)"
                  : "rgba(0, 0, 0, 0.87)",
              lineHeight: 1.3,
              cursor: "pointer",
            }}
          >
            {product.Product_Full_Name}
          </Typography>
        </Tooltip>

        {/* Remove the rating box from here */}

        <Typography
          variant="body2"
          sx={{
            mb: 0.75,
            display: "-webkit-box",
            WebkitLineClamp: 1, // Changed to 1 line
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontSize: "0.8rem",
            color: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.7)"
                : "rgba(0, 0, 0, 0.6)",
            lineHeight: 1.4,
          }}
        >
          {product.Short_Description}
        </Typography>

        {/* Combination badge section */}
        {context.state.appSettings?.some(
          (setting) =>
            setting.Setting_Name === "Show Combination" &&
            setting.Setting_Value === "YES"
        ) &&
          product.Default_Combination?.Combination_Text && (
            <Box
              sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}
            >
              <Tooltip
                title={
                  getOtherCombinations().length > 0
                    ? getCombinationTooltipContent()
                    : "Only one combination available"
                }
                placement="right-start"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      maxWidth: 320,
                      fontSize: "0.8rem",
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(40, 40, 40, 0.98)"
                          : "rgba(60, 60, 60, 0.98)",
                      backdropFilter: "blur(12px)",
                      border: (theme) =>
                        theme.palette.mode === "dark"
                          ? "1px solid rgba(255, 255, 255, 0.1)"
                          : "1px solid rgba(255, 255, 255, 0.2)",
                      borderRadius: "8px",
                      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
                      p: 1.5,
                    },
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    cursor: "pointer",
                    position: "relative",
                    "&:hover": {
                      transform: "scale(1.02)",
                    },
                  }}
                >
                  <Chip
                    label={renderCombinationChip(
                      product.Default_Combination.Combination_Text,
                      true
                    )}
                    size="medium"
                    sx={{
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.08)"
                          : "rgba(0, 0, 0, 0.06)",
                      color: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.9)"
                          : "rgba(0, 0, 0, 0.8)",
                      fontWeight: 500,
                      fontSize: "0.75rem",
                      border: "1px solid",
                      borderColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.12)"
                          : "rgba(0, 0, 0, 0.12)",
                      height: 28,
                      "& .MuiChip-label": {
                        px: 1.5,
                        py: 0.5,
                        display: "flex",
                        alignItems: "center",
                      },
                    }}
                  />

                  {getOtherCombinations().length > 0 && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: -8,
                        right: -12,
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? theme.palette.info.dark
                            : theme.palette.info.main,
                        color: "white",
                        borderRadius: "6px",
                        minWidth: 45,
                        height: 20,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.65rem",
                        fontWeight: 600,
                        boxShadow: (theme) =>
                          theme.palette.mode === "dark"
                            ? "0 2px 4px rgba(0, 0, 0, 0.3)"
                            : "0 2px 4px rgba(0, 0, 0, 0.15)",
                        border: "2px solid",
                        borderColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(18, 18, 20, 0.95)"
                            : "rgba(255, 255, 255, 0.98)",
                        px: 0.75,
                      }}
                    >
                      +{getOtherCombinations().length} more
                    </Box>
                  )}
                </Box>
              </Tooltip>
            </Box>
          )}

        <Box sx={{ mt: "auto" }}>
          {/* New ratings and price box - place just above Add to Cart button */}
          <Box
            sx={{
              display: "flex", // Always show on all screen sizes
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1.5,
              flexDirection: { xs: "column", sm: "row" }, // Stack vertically on mobile, horizontally on larger screens
              gap: { xs: 1, sm: 0 }, // Add gap on mobile when stacked
            }}
          >
            {/* Ratings on the left (desktop) / top (mobile) */}
            <Box
              sx={{
                display: { xs: "none", sm: "flex" }, // Hide ratings on mobile (shown in image overlay)
                flexDirection: "column",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Rating
                  value={product.averageRating || 0}
                  precision={0.5}
                  readOnly
                  size="small"
                  sx={{ fontSize: "0.9rem" }}
                />
                {product.averageRating > 0 ? (
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{ ml: 0.5, fontSize: "0.75rem", fontWeight: 500 }}
                  >
                    {product.averageRating.toFixed(1)}
                  </Typography>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 0.5, fontSize: "0.75rem" }}
                  >
                    0.0
                  </Typography>
                )}
              </Box>
              {product.reviewCount > 0 ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.7rem", mt: 0.25 }}
                >
                  from {product.reviewCount} verified review
                  {product.reviewCount !== 1 ? "s" : ""}
                </Typography>
              ) : (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.7rem", mt: 0.25 }}
                >
                  No reviews yet
                </Typography>
              )}
            </Box>

            {/* Price on the right (desktop) / bottom (mobile) - Always visible */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end", // Always right-align on all screen sizes
                width: { xs: "100%", sm: "auto" }, // Full width on mobile
              }}
            >
              {/* Show MRP price if it's different from selling price */}
              {product.Default_Inventory?.Total_MRP_Price >
                product.Default_Inventory?.Total_Selling_Price && (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: "0.8rem", sm: "0.9rem" }, // Slightly smaller on mobile
                    color: "text.secondary",
                    textDecoration: "line-through",
                    lineHeight: 1,
                    mb: 0.25,
                    textAlign: "right", // Always right-align on all screen sizes
                  }}
                >
                  <small
                    style={{
                      fontWeight: "400",
                      fontSize: "0.7rem",
                      marginRight: "2px",
                    }}
                  >
                    {context.state?.companyInfo?.Currency}
                  </small>
                  {product.Default_Inventory?.Total_MRP_Price?.toFixed(2)}
                </Typography>
              )}
              {/* Selling price */}
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: "1rem", sm: "1.1rem" }, // Slightly smaller on mobile
                  fontWeight: "bold",
                  color: "primary.main",
                  textAlign: "right", // Always right-align on all screen sizes
                  lineHeight: 1,
                }}
              >
                <small
                  style={{
                    fontWeight: "500",
                    fontSize: "0.8rem",
                    marginRight: "2px",
                  }}
                >
                  {context.state?.companyInfo?.Currency}
                </small>
                {product.Default_Inventory?.Total_Selling_Price?.toFixed(2)}
              </Typography>
            </Box>
          </Box>

          <Button
            variant="outlined"
            color="primary"
            fullWidth
            startIcon={<AddShoppingCartIcon className="button-icon" />}
            sx={{
              borderRadius: 2,
              py: { xs: 0.5, sm: 0.8 }, // Smaller padding on mobile
              textTransform: "none",
              fontWeight: 600,
              borderWidth: "2px",
              position: "relative",
              overflow: "hidden",
              background: "linear-gradient(45deg, transparent, transparent)",
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              whiteSpace: "nowrap",
              // Mobile: smaller button style
              fontSize: { xs: "0.8rem", sm: "0.9rem" },
              minHeight: { xs: "36px", sm: "42px" },
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: "-100%",
                width: "100%",
                height: "100%",
                background: (theme) =>
                  `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 50%, ${theme.palette.primary.light} 100%)`,
                transition: "left 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                zIndex: -1,
              },
              "&:hover": {
                color: "white",
                borderColor: "transparent",
                transform: "translateY(-2px)",
                boxShadow: (theme) =>
                  `0 12px 30px ${theme.palette.primary.main}40, 0 6px 20px ${theme.palette.secondary.main}30`,
                "&::before": {
                  left: 0,
                },
                "& .button-icon": {
                  transform: "translateX(8px) scale(1.1)",
                  opacity: 0,
                },
                "& .button-text": {
                  transform: "translateX(-8px)",
                },
                "& .hover-icon": {
                  transform: "translateX(0px) scale(1.1)",
                  opacity: 1,
                },
              },
              "&:active": {
                transform: "translateY(0px)",
              },
              "& .button-icon": {
                fontSize: "1.25rem",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                transform: "translateX(0px) scale(1)",
                opacity: 1,
              },
              "& .button-text": {
                transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                transform: "translateX(0px)",
                display: "inline",
              },
              "& .hover-icon": {
                position: "absolute",
                left: "16px",
                fontSize: "1.25rem",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                transform: "translateX(-8px) scale(1)",
                opacity: 0,
              },
            }}
            onClick={addToCart}
          >
            <ArrowForwardIcon className="hover-icon" />
            <span className="button-text">
              {hasChoiceOptions() && Object.keys(selectedChoices).length === 0
                ? "Select Options"
                : "Add to Cart"}
            </span>
          </Button>
        </Box>
      </CardContent>

      {/* ProductQuickView Component */}
      <ProductQuickView
        open={quickViewOpen}
        onClose={() => setQuickViewOpen(false)}
        product={product}
        context={context}
        wishlist={wishlist}
        onAddToCart={addToCart}
        onToggleWishlist={toggleWishlist}
        getImageUrl={(product, index) =>
          product?.Default_Media && product.Default_Media[index]
            ? `${process.env.BASEURL}${product.Default_Media[index]}`
            : ""
        }
        getAverageRating={(product) => product?.averageRating || 0}
        getReviewCount={(product) => product?.reviewCount || 0}
      />

      <SnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        options={snackOptions}
      />

      {/* Product Choice Options Selector Dialog */}
      <ProductChoiceSelector
        open={showChoiceSelector}
        onClose={() => setShowChoiceSelector(false)}
        product={product}
        selectedCombination={product?.Default_Combination}
        units={units}
        onChoicesSelected={handleChoicesSelected}
        onAddToCart={performCartAdd}
        resetAndPreview={resetAndPreview}
        isProductAddonsEnabled={isProductAddonsEnabled}
        checkProductAddons={checkProductAddons}
        setShowAddonSelector={setShowAddonSelector}
        setParentSelectedChoices={setSelectedChoices}
      />

      {/* Product Addons Selector Dialog */}
      <ProductAddonsSelector
        open={showAddonSelector}
        onClose={() => setShowAddonSelector(false)}
        product={product}
        selectedCombination={product?.Default_Combination}
        units={units}
        onAddToCartComplete={handleAddonCartComplete}
        mainProductPrice={product?.Default_Inventory?.Total_Selling_Price || 0}
        selectedChoices={selectedChoices}
      />
    </Card>
  );
}
